import React from "react";
import { api } from "../lib/api";
import {
    Tabs,
    TabsList,
    TabsTrigger,
    TabsContent,
} from "./ui/tabs";
import { TrendingUp, ChevronRight } from "lucide-react";

const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

export default function CreatorPerformance() {
    const [data, setData] = React.useState(null);
    const [openCampaign, setOpenCampaign] = React.useState(null);

    React.useEffect(() => {
        api.get("/creator/performance").then((r) => setData(r.data));
    }, []);

    if (!data) return <div className="text-slate-500 overline">Loading…</div>;

    const empty = data.by_channel.length === 0;

    return (
        <div className="space-y-6" data-testid="creator-performance">
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                    ["Revenue (your videos)", fmt(data.total_revenue)],
                    ["Your earnings", fmt(data.total_earnings), "coral"],
                    ["Active campaigns", data.by_campaign.length, "lime"],
                ].map(([l, v, accent]) => (
                    <div key={l} className="card-ecom p-4">
                        <div className="text-xs text-slate-500">{l}</div>
                        <div
                            className={`font-display text-2xl font-semibold mt-1 ${
                                accent === "coral" ? "text-coral" : accent === "lime" ? "text-lime-ink" : ""
                            }`}
                        >
                            {v}
                        </div>
                    </div>
                ))}
            </div>

            <div className="card-ecom p-3 flex items-center justify-end">
                <span className="chip chip-lime">
                    <TrendingUp className="w-3 h-3" /> MOCKED · MVP
                </span>
            </div>

            {empty ? (
                <div className="card-ecom p-12 text-center text-slate-500" data-testid="creator-perf-empty">
                    No performance yet. Earnings start when a brand pushes a rev-share campaign.
                </div>
            ) : (
                <Tabs defaultValue="channels">
                    <TabsList className="rounded-full bg-slate-100 p-1 h-auto" data-testid="creator-perf-subtabs">
                        {[
                            ["channels", "Channels"],
                            ["campaigns", "Campaigns"],
                            ["videos", "Videos"],
                        ].map(([v, l]) => (
                            <TabsTrigger
                                key={v}
                                value={v}
                                className="rounded-full data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm px-5 py-2"
                                data-testid={`creator-perf-tab-${v}`}
                            >
                                {l}
                            </TabsTrigger>
                        ))}
                    </TabsList>

                    <TabsContent value="channels" className="mt-5">
                        <div className="card-ecom overflow-hidden" data-testid="creator-perf-channels">
                            <div className="grid grid-cols-12 gap-3 px-5 py-3 border-b hairline bg-slate-50 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                                <div className="col-span-5">Channel</div>
                                <div className="col-span-3 text-right">Spend</div>
                                <div className="col-span-2 text-right">Revenue</div>
                                <div className="col-span-2 text-right">You earned</div>
                            </div>
                            {data.by_channel.map((c) => (
                                <div
                                    key={c.channel}
                                    className="grid grid-cols-12 gap-3 px-5 py-4 border-b hairline last:border-0 items-center"
                                    data-testid={`creator-row-ch-${c.channel}`}
                                >
                                    <div className="col-span-5">
                                        <span className="chip chip-lime">{c.channel}</span>
                                    </div>
                                    <div className="col-span-3 text-right text-sm text-slate-700">
                                        {fmt(c.spend)}
                                    </div>
                                    <div className="col-span-2 text-right font-display font-semibold">
                                        {fmt(c.revenue)}
                                    </div>
                                    <div className="col-span-2 text-right font-display font-semibold text-coral">
                                        +{fmt(c.earnings)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </TabsContent>

                    <TabsContent value="campaigns" className="mt-5">
                        <div className="card-ecom overflow-hidden" data-testid="creator-perf-campaigns">
                            <div className="grid grid-cols-12 gap-3 px-5 py-3 border-b hairline bg-slate-50 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                                <div className="col-span-6">Campaign</div>
                                <div className="col-span-2 text-right">Rev share</div>
                                <div className="col-span-2 text-right">Revenue</div>
                                <div className="col-span-2 text-right">You earned</div>
                            </div>
                            {data.by_campaign.map((r) => (
                                <React.Fragment key={r.campaign_id}>
                                    <div
                                        onClick={() =>
                                            setOpenCampaign(
                                                openCampaign === r.campaign_id ? null : r.campaign_id,
                                            )
                                        }
                                        className="grid grid-cols-12 gap-3 px-5 py-4 border-b hairline last:border-0 items-center cursor-pointer hover:bg-slate-50"
                                        data-testid={`creator-row-camp-${r.campaign_id}`}
                                    >
                                        <div className="col-span-6 flex items-center gap-2">
                                            <ChevronRight
                                                className={`w-4 h-4 text-slate-400 transition-transform ${openCampaign === r.campaign_id ? "rotate-90" : ""}`}
                                            />
                                            <div>
                                                <div className="font-display font-semibold text-slate-900 truncate">
                                                    {r.campaign_name}
                                                </div>
                                                <div className="text-xs text-slate-500">{r.brand_name}</div>
                                            </div>
                                        </div>
                                        <div className="col-span-2 text-right text-sm text-slate-700">
                                            {r.rev_share_pct}%
                                        </div>
                                        <div className="col-span-2 text-right font-display font-semibold">
                                            {fmt(r.total_revenue)}
                                        </div>
                                        <div className="col-span-2 text-right font-display font-semibold text-coral">
                                            +{fmt(r.total_earnings)}
                                        </div>
                                    </div>
                                    {openCampaign === r.campaign_id && (
                                        <div className="bg-slate-50 px-5 py-4 border-b hairline space-y-2">
                                            <div className="overline text-slate-500">
                                                BY CHANNEL
                                            </div>
                                            <div className="grid grid-cols-12 gap-3 text-xs text-slate-500">
                                                <div className="col-span-6">Channel</div>
                                                <div className="col-span-2 text-right">Revenue</div>
                                                <div className="col-span-2 text-right">ROAS</div>
                                                <div className="col-span-2 text-right">Earned</div>
                                            </div>
                                            {r.channels.map((b) => (
                                                <div
                                                    key={b.channel}
                                                    className="grid grid-cols-12 gap-3 text-sm"
                                                >
                                                    <div className="col-span-6">
                                                        <span className="chip chip-lime">{b.channel}</span>
                                                    </div>
                                                    <div className="col-span-2 text-right text-slate-700">
                                                        {fmt(b.revenue)}
                                                    </div>
                                                    <div className="col-span-2 text-right text-slate-700">
                                                        {b.roas}x
                                                    </div>
                                                    <div className="col-span-2 text-right text-coral font-semibold">
                                                        +{fmt(b.earning)}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    </TabsContent>

                    <TabsContent value="videos" className="mt-5">
                        {data.by_video.length === 0 ? (
                            <div className="card-ecom p-12 text-center text-slate-500">
                                No video revenue yet.
                            </div>
                        ) : (
                            <div className="card-ecom overflow-hidden" data-testid="creator-perf-videos">
                                <div className="grid grid-cols-12 gap-3 px-5 py-3 border-b hairline bg-slate-50 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                                    <div className="col-span-6">Video</div>
                                    <div className="col-span-2">Channels</div>
                                    <div className="col-span-2 text-right">Revenue</div>
                                    <div className="col-span-2 text-right">You earned</div>
                                </div>
                                {data.by_video.map((v) => (
                                    <div
                                        key={v.video_id}
                                        className="grid grid-cols-12 gap-3 px-5 py-4 border-b hairline last:border-0 items-center"
                                        data-testid={`creator-row-video-${v.video_id}`}
                                    >
                                        <div className="col-span-6">
                                            <div className="font-display font-semibold text-slate-900 truncate">
                                                {v.title}
                                            </div>
                                            <div className="text-xs text-slate-500">
                                                {v.campaigns} campaigns
                                            </div>
                                        </div>
                                        <div className="col-span-2 flex flex-wrap gap-1">
                                            {(v.channels || []).slice(0, 2).map((ch) => (
                                                <span key={ch} className="chip chip-lime">{ch}</span>
                                            ))}
                                            {v.channels.length > 2 && (
                                                <span className="chip">+{v.channels.length - 2}</span>
                                            )}
                                        </div>
                                        <div className="col-span-2 text-right font-display font-semibold">
                                            {fmt(v.revenue)}
                                        </div>
                                        <div className="col-span-2 text-right font-display font-semibold text-coral">
                                            +{fmt(v.earnings)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </TabsContent>
                </Tabs>
            )}
        </div>
    );
}
